import { z } from 'zod';
import { isHarBodyBase64, resolveHarView } from '@terra/shared-utils';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';
import type {
  HarEntry,
  HarEntryDecodeStatusType,
} from '@terra/shared-schemas-tracker';
import type { Request, Response } from 'har-format';
import type { BodyEncoding } from '../../electron-shared/utils/binary-body';
import { buildStoredHarDisplayEntry } from '../../utils/harGrpcDisplay.utils';

export type HARRequest = {
  id: string;
  method: string;
  url: string;
  status: number;
  responseTime: number;
  timestamp: string;
  size: number;
  contentType: string;
  headers: {
    request: Record<string, string | string[]>;
    response: Record<string, string | string[]>;
  };
  rawHeaders: {
    request?: string[];
    response?: string[];
  };
  requestBody?: Record<string, unknown> | string;
  requestBodyEncoding?: BodyEncoding;
  responseBody: Record<string, unknown> | string;
  displayRequestBody?: string;
  displayResponseBody?: string;
  wireRequestBody?: string;
  wireResponseBody?: string;
  decodeStatus?: HarEntryDecodeStatusType | null;
  hasDecodedBodyView?: boolean;
  grpcHarDisplay?: StoredHarDisplayEntry;
  httpVersion?: string;
  responseHttpVersion?: string;
  webSocketMessages?: unknown;
};

type HarHeaderForDisplay = {
  name: string;
  value: string | string[];
  comment?: string;
};

type HarRequestForDisplay = {
  method: Request['method'];
  url: Request['url'];
  httpVersion?: Request['httpVersion'];
  headers: HarHeaderForDisplay[];
  postData?: {
    text?: string;
    comment?: string;
    mimeType?: string;
  };
};

type HarResponseForDisplay = {
  status: Response['status'];
  httpVersion?: Response['httpVersion'];
  headers: HarHeaderForDisplay[];
  content?: {
    size?: number;
    mimeType?: string;
    text?: string | null;
  };
};

const HarHeaderForDisplaySchema = z.object({
  name: z.string(),
  value: z.union([z.string(), z.array(z.string())]),
  comment: z.string().optional(),
});

const HarRequestForDisplaySchema = z.object({
  method: z.string(),
  url: z.string(),
  httpVersion: z.string().optional(),
  headers: HarHeaderForDisplaySchema.array().default([]),
  postData: z
    .object({
      text: z.string().optional(),
      comment: z.string().optional(),
      mimeType: z.string().optional(),
    })
    .optional(),
});

const HarResponseForDisplaySchema = z.object({
  status: z.number(),
  httpVersion: z.string().optional(),
  headers: HarHeaderForDisplaySchema.array().default([]),
  content: z
    .object({
      size: z.number().optional(),
      mimeType: z.string().optional(),
      text: z.string().nullable().optional(),
    })
    .optional(),
});

const HarTimingForDisplaySchema = z
  .object({
    wait: z.number().optional(),
    receive: z.number().optional(),
  })
  .nullable()
  .optional();

const parseHarRequestForDisplay = (
  value: unknown,
): HarRequestForDisplay | undefined => {
  const parsed = HarRequestForDisplaySchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
};

const parseHarResponseForDisplay = (
  value: unknown,
): HarResponseForDisplay | undefined => {
  const parsed = HarResponseForDisplaySchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
};

const harHeaderValues = (header: HarHeaderForDisplay): string[] =>
  Array.isArray(header.value) ? header.value : [header.value];

const harHeadersToRecord = (
  headers: HarHeaderForDisplay[],
): Record<string, string | string[]> => {
  const record: Record<string, string | string[]> = {};

  for (const header of headers) {
    for (const value of harHeaderValues(header)) {
      const existingValue = record[header.name];

      if (!existingValue) {
        record[header.name] = value;
        continue;
      }

      if (Array.isArray(existingValue)) {
        record[header.name] = [...existingValue, value];
        continue;
      }

      record[header.name] = [existingValue, value];
    }
  }

  return record;
};

const harHeadersToRawHeaders = (
  headers: HarHeaderForDisplay[],
): string[] | undefined => {
  if (!headers.length) {
    return undefined;
  }

  return headers.flatMap((header) =>
    harHeaderValues(header).flatMap((value) => [header.name, value]),
  );
};

const parseMaybeJsonBody = (
  text: string | null | undefined,
): Record<string, unknown> | string | undefined => {
  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const mapHarEntryToHarRequest = (harEntry: HarEntry): HARRequest => {
  const parsedTimings = HarTimingForDisplaySchema.safeParse(harEntry.timings);
  const request = parseHarRequestForDisplay(harEntry.request);
  const response = parseHarResponseForDisplay(harEntry.response);
  const timings = parsedTimings.success ? parsedTimings.data : undefined;
  const requestHarHeaders = request?.headers ?? [];
  const responseHarHeaders = response?.headers ?? [];
  const requestHeaders = harHeadersToRecord(requestHarHeaders);
  const responseHeaders = harHeadersToRecord(responseHarHeaders);
  const requestRawHeaders = harHeadersToRawHeaders(requestHarHeaders);
  const responseRawHeaders = harHeadersToRawHeaders(responseHarHeaders);
  const responseTime = timings
    ? (timings.wait || 0) + (timings.receive || 0)
    : 0;
  const view = resolveHarView({ harEntry });
  const wireRequestBody = request?.postData?.text;
  const wireResponseBody = response?.content?.text ?? '';
  const hasDecodedBodyView =
    view.preferred.request.source === 'decoded' ||
    view.preferred.response.source === 'decoded';
  const parsedRequestBody = parseMaybeJsonBody(wireRequestBody);
  const parsedResponseBody = parseMaybeJsonBody(wireResponseBody);
  const displayRequestBody =
    view.preferred.request.source === 'decoded'
      ? view.preferred.request.body
      : undefined;
  const displayResponseBody =
    view.preferred.response.source === 'decoded'
      ? view.preferred.response.body
      : undefined;
  const grpcHarDisplay = buildStoredHarDisplayEntry(harEntry);

  return {
    id: harEntry.id,
    method: request?.method || 'GET',
    url: request?.url || '',
    status:
      response?.status !== null && response?.status !== undefined
        ? response.status
        : 0,
    responseTime,
    timestamp: harEntry.createdAt
      ? typeof harEntry.createdAt === 'string'
        ? harEntry.createdAt
        : new Date(harEntry.createdAt).toISOString()
      : new Date().toISOString(),
    size: response?.content?.size || 0,
    contentType: response?.content?.mimeType || 'application/octet-stream',
    headers: {
      request: requestHeaders,
      response: responseHeaders,
    },
    rawHeaders: {
      request: requestRawHeaders,
      response: responseRawHeaders,
    },
    requestBody: parsedRequestBody,
    requestBodyEncoding: isHarBodyBase64({
      text: wireRequestBody,
      comment: request?.postData?.comment,
      mimeType: request?.postData?.mimeType ?? '',
    })
      ? ('base64' as const)
      : undefined,
    responseBody: parsedResponseBody ?? '',
    displayRequestBody,
    displayResponseBody,
    wireRequestBody,
    wireResponseBody,
    decodeStatus: view.decodeStatus,
    hasDecodedBodyView,
    grpcHarDisplay,
    httpVersion: request?.httpVersion,
    responseHttpVersion: response?.httpVersion,
    webSocketMessages: harEntry.webSocketMessages ?? undefined,
  };
};
